import BaseProduct from './BaseProduct';
import { ProductId } from '../types';
import store from '../state';
import { getTileUrl, getApiServer } from '../config';

export class AuroraLayer extends BaseProduct {
  public readonly id: ProductId = 'aurora';
  private sourceId = 'aurora_viewline-source';

  protected onActivate(): void {
    this.refresh();
  }

  protected onDeactivate(): void {
    this.layerManager.removeLayer('darkskies_aurora');
    if (this.map.getLayer('aurora_viewline')) this.map.removeLayer('aurora_viewline');
    if (this.map.getSource(this.sourceId)) this.map.removeSource(this.sourceId);
  }

  protected onTimeUpdate(_date: string, _hour: string): void {
    this.refresh();
  }

  private getAuroraTargetDate(d: Date, tz?: string): string {
    const timeZone = tz || ((typeof Intl !== 'undefined' && Intl.DateTimeFormat)
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : 'America/Los_Angeles');

    const dFmt = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: 'numeric',
      hour12: false
    });
    const parts = dFmt.formatToParts(d);
    const yr = parseInt(parts.find(p => p.type === 'year')?.value || '2026', 10);
    const mo = parseInt(parts.find(p => p.type === 'month')?.value || '8', 10);
    const dy = parseInt(parts.find(p => p.type === 'day')?.value || '5', 10);
    const hr = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);

    const localDate = new Date(Date.UTC(yr, mo - 1, dy));
    if (hr < 12) {
      localDate.setUTCDate(localDate.getUTCDate() - 1);
    }
    return localDate.toISOString().split('T')[0];
  }

  public refresh() {
    if (!this.active) return;
    const state = store.getState();
    const manifest = state.manifest;
    const current = state.currentTimeStep;
    if (!current || !manifest) return;

    const userTz = state.userTimezone;
    const auroraDateStr = this.getAuroraTargetDate(current, userTz);
    const isAvailable = state.auroraAvailableDates.length === 0 || state.auroraAvailableDates.includes(auroraDateStr);

    if (isAvailable) {
      const auroraTiles = [
        getTileUrl(manifest, 'aurora', '', auroraDateStr, '')
      ];
      // Draw aurora raster overlay
      this.layerManager.updateRasterLayer('darkskies_aurora', auroraTiles, 0.95, undefined, 0, 5);

      const apiServer = getApiServer(manifest);
      const tileRoot = (manifest.tile_base || 'tiles').replace(/^\/+|\/+$/g, '');
      const cleanServer = apiServer.replace(/\/$/, '');
      let cleanPath = `${tileRoot}/observed/aurora/aurora_viewline_${auroraDateStr}.json`.replace(/\/+/g, '/');
      if (cleanPath.startsWith('/')) {
        cleanPath = cleanPath.substring(1);
      }
      const viewlineUrl = `${cleanServer}/${cleanPath}`;

      const viewlineSource = this.map.getSource(this.sourceId) as any;
      if (!viewlineSource) {
        this.map.addSource(this.sourceId, {
          type: 'geojson',
          data: viewlineUrl
        });
      } else {
        viewlineSource.setData(viewlineUrl);
      }

      if (!this.map.getLayer('aurora_viewline')) {
        this.map.addLayer({
          id: 'aurora_viewline',
          type: 'line',
          source: this.sourceId,
          paint: {
            'line-color': '#ffffff',
            'line-width': 2,
            'line-dasharray': [4, 4],
            'line-opacity': 0.95
          }
        });
        (this.layerManager as any).activeLayers.add('aurora_viewline');
      } else {
        this.map.setPaintProperty('aurora_viewline', 'line-opacity', 0.95);
      }
    } else {
      this.layerManager.removeLayer('darkskies_aurora');
      if (this.map.getLayer('aurora_viewline')) this.map.removeLayer('aurora_viewline');
    }
  }
}
export default AuroraLayer;
